
import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import * as auth0 from 'auth0-js';
import * as _ from 'lodash';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/internal/operators';
import {catchError, map, switchMap, withLatestFrom} from 'rxjs/operators';

import {debug} from '../../../rxjs-operators';
import {error} from '../actions/toasts';
import {ChangeClientAction} from '../actions/user-context';
import {LoadUserInfoFailAction, LoadUserInfoSuccessAction, LogoutAction, userInfoActionTypes} from '../actions/user-info';
import {CUSTOM_CLAIMS_DEFAULT_CLIENT_ID} from '../consts';
import {Auth0UserInfo} from '../models/auth0-user-info';
import {RouterStateUrl} from '../models/router-state-url';
import * as fromRoot from '../reducers';
import {Auth0AuthenticationService} from '../services/auth0-authentication.service';
import {L10nTranslationService} from 'angular-l10n';

@Injectable()
export class UserInfoEffect {

  load$ = createEffect(() => { return this.actions$.pipe(
              ofType(userInfoActionTypes.LOAD),
              debug('Load user info action received'),
              switchMap(() => this.authService.getUserInfo().pipe(
                              withLatestFrom(this.store.select(fromRoot.selectors.getRouterState)),
                              switchMap(([userInfo, routerState]) =>
                                          of<Action>(new LoadUserInfoSuccessAction(userInfo),
                                          new ChangeClientAction({ clientId: this.getStoredClient(userInfo, routerState), redirect: false }))),
                              catchError(res => this.handleAuth0Error(res)),
                              )))
  });


  reload$ = createEffect(() => { return this.actions$.pipe(
                ofType(userInfoActionTypes.RELOAD),
                debug('Reload user info action received.'),
                switchMap(() => this.authService.getUserInfo(true).pipe(
                                map((payload: Auth0UserInfo) => new LoadUserInfoSuccessAction(payload)),
                                catchError(() => of(new LoadUserInfoFailAction())),
                                )))
  });


  loadFail$ = createEffect(() => { return this.actions$.pipe(
                  ofType(userInfoActionTypes.LOAD_FAIL),
                  debug('Auth0 /userinfo error (in payload):'),
                      map((action: LoadUserInfoFailAction) =>
                            error(this.translation.translate('AUTH_GET_USER_INFO_ERROR', action.payload),
                              this.translation.translate('TOAST_ERROR_TITLE'))))
  });


  logout$ = createEffect(() => { return this.actions$.pipe(
    ofType(userInfoActionTypes.LOGOUT),
    debug('Logout action received.'),
    tap((action: LogoutAction) => this.authService.logout(action.payload))
  )}, { dispatch: false });

  constructor(private actions$: Actions,
              private translation: L10nTranslationService,
              private authService: Auth0AuthenticationService,
              private store: Store<fromRoot.AppState>) {}

  private handleAuth0Error(err: auth0.Auth0Error): Observable<Action> { return of(new LoadUserInfoFailAction(err)); }

  private getStoredClient(userInfo: Auth0UserInfo, routerState: RouterStateUrl): string {
    if (!_.isEmpty(routerState) && _.has(routerState, ['params', 'clientId'])) {
      return _.get(routerState, ['params', 'clientId']);
    }

    return userInfo[CUSTOM_CLAIMS_DEFAULT_CLIENT_ID];
  }
}
